import { createContext, ReactNode, use, useMemo } from "react";
import { Loaded, mapLoaded } from "models/loaded";
import { SyncedSearchState, useSyncedSearchState } from "./search";
import {
  SearchChemicalElements,
  SearchChemistryGroups,
  SearchMaterialChemistry,
  SearchMaterialPhysics,
  SearchMaterials,
  SearchProductGroups,
  SearchSteelGrades,
  SyncedContextData,
} from "./context";
import { BasketRead } from "src/store/api/generatedApi";

const SearchContext = createContext<SyncedSearchState | undefined>(undefined);

export const SearchContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const value = useSyncedSearchState();
  return <SearchContext value={value}>{children}</SearchContext>;
};

export const useSearch = (): SyncedSearchState => {
  const search = use(SearchContext);

  if (search == null) {
    throw new Error("No search context has been provided");
  } else {
    return search;
  }
};
const useSearchContext = <T,>(selector: (context: SyncedContextData) => T) => {
  const context = useSearch().context;
  return useMemo(
    () => mapLoaded(context, selector),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- every caller supplies a call-site-stable selector
    [context]
  );
};

export const useProductGroupsId = () =>
  useSearchContext(({ productGroupsId }) => productGroupsId);
export const useSteelGradesId = () =>
  useSearchContext(({ steelGradesId }) => steelGradesId);
export const useMaterialsId = () =>
  useSearchContext(({ materialsId }) => materialsId);
export const useMaterialPhysicsId = () =>
  useSearchContext(({ materialPhysicsId }) => materialPhysicsId);

export const useMaterials = (): Loaded<SearchMaterials> =>
  useSearchContext(({ materials }) => materials);

export const useMaterialPhysics = (): Loaded<SearchMaterialPhysics> =>
  useSearchContext(({ materialPhysics }) => materialPhysics);

export const useMaterialChemistry = (): Loaded<SearchMaterialChemistry> =>
  useSearchContext(({ materialChemistry }) => materialChemistry);

export const useProductGroups = (): Loaded<SearchProductGroups> =>
  useSearchContext(({ productGroups }) => productGroups);

export const useChemistryGroups = (): Loaded<SearchChemistryGroups> =>
  useSearchContext(({ chemistryGroups }) => chemistryGroups);

export const useSteelGrades = (): Loaded<SearchSteelGrades> =>
  useSearchContext(({ steelGrades }) => steelGrades);

export const useChemicalElements = (): Loaded<SearchChemicalElements> =>
  useSearchContext(({ chemicalElements }) => chemicalElements);

export const useBaskets = (): Loaded<BasketRead[]> =>
  useSearchContext(({ baskets }) => baskets);

export const useHasDefaultSets = () =>
  useSearchContext(({ hasDefaultSets }) => hasDefaultSets);
